import Ionicons from "@expo/vector-icons/Ionicons";
import type { ReactNode } from "react";
import { Pressable, StyleSheet, useWindowDimensions, View } from "react-native";

import { ThemedText } from "@/components/themed-text";
import { Pager, usePagedItems } from "@/components/ui/pager";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import i18n from "@/i18n";
import type { ProgressDashboard } from "@/types";

export type ProgressDetailKey =
  | "practice"
  | "goals"
  | "reflections";

function relativeDate(iso: string): string {
  const elapsed = Math.max(0, Date.now() - new Date(iso).getTime());
  const days = Math.floor(elapsed / 86_400_000);
  if (days === 0) return i18n.t("growth.story.relative.today");
  if (days === 1) return i18n.t("growth.story.relative.yesterday");
  return i18n.t("growth.story.relative.daysAgo", { count: days });
}

function dateTime(iso: string): string {
  return new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(iso));
}

/** "1 August" — the completion date, without the time of day. */
function completionDate(iso: string): string {
  return new Intl.DateTimeFormat(i18n.resolvedLanguage, {
    day: "numeric",
    month: "long",
  }).format(new Date(iso));
}

/**
 * A KEY per card, resolved at render.
 *
 * This table used to hold "Practice" / "Goals" / "Reflections" outright, and
 * an English `plural()` helper sat above it appending an "s" — neither of
 * which any other language can use. i18next already knows every locale's
 * plural categories, including the "many" that Spanish, Portuguese and French
 * need and English does not have.
 */
const CARD_META: Record<
  ProgressDetailKey,
  { labelKey: string; icon: keyof typeof Ionicons.glyphMap }
> = {
  practice: {
    labelKey: "growth.story.cards.practice",
    icon: "chatbubbles-outline",
  },
  goals: { labelKey: "growth.story.cards.goals", icon: "checkmark-done-outline" },
  reflections: {
    labelKey: "growth.story.cards.reflections",
    icon: "book-outline",
  },
};

export function ProgressStoryGrid({
  data,
  onSelect,
}: {
  data: ProgressDashboard;
  onSelect: (detail: ProgressDetailKey) => void;
}) {
  const { width } = useWindowDimensions();
  const c = useTheme();
  const threeColumns = width >= 900;
  const latestReflection = data.reflections.recent[0];

  const cards: {
    key: ProgressDetailKey;
    primary: string;
    supporting: string[];
    empty?: boolean;
  }[] = [
    {
      key: "practice",
      primary:
        data.practice.completed > 0
          ? i18n.t("growth.story.practice.completedCount", {
              count: data.practice.completed,
            })
          : i18n.t("growth.story.practice.none"),
      supporting:
        data.practice.completed > 0
          ? [
              i18n.t("growth.story.practice.activeCount", {
                count: data.practice.activePractice,
              }),
              data.practice.mostPractised
                ? i18n.t("growth.story.practice.mostPractised", {
                    label: data.practice.mostPractised.label,
                  })
                : "",
            ].filter(Boolean)
          : [i18n.t("growth.story.practice.emptyBody")],
      empty: data.practice.completed === 0,
    },
    {
      key: "goals",
      primary: i18n.t("growth.story.goals.completedCount", {
        count: data.goals.completed,
      }),
      supporting: [
        i18n.t("growth.story.goals.startedCount", { count: data.goals.started }),
        i18n.t("growth.story.goals.activeNowCount", {
          count: data.goals.activeNow,
        }),
      ],
    },
    {
      key: "reflections",
      primary: i18n.t("growth.story.reflections.loggedCount", {
        count: data.reflections.total,
      }),
      supporting: [
        i18n.t("growth.story.reflections.outsideCount", {
          count: data.reflections.outsideOpncue,
        }),
        latestReflection
          ? i18n.t("growth.story.reflections.latest", {
              when: relativeDate(latestReflection.createdAt),
            })
          : "",
      ].filter(Boolean),
      empty: data.reflections.total === 0,
    },
  ];

  return (
    <View style={styles.grid}>
      {cards.map((card) => {
        const meta = CARD_META[card.key];
        return (
          <Pressable
            key={card.key}
            accessibilityRole="button"
            accessibilityLabel={i18n.t("growth.story.openProgressA11y", {
              label: i18n.t(meta.labelKey),
            })}
            onPress={() => onSelect(card.key)}
            style={({ pressed }) => [
              styles.card,
              threeColumns ? styles.cardThreeColumn : styles.cardSingleColumn,
              pressed && styles.pressed,
            ]}
          >
            <StoryCardSurface>
              <View style={styles.cardHeader}>
                <IconTile name={meta.icon} />
                <ThemedText variant="label" tone="muted">
                  {i18n.t(meta.labelKey).toUpperCase()}
                </ThemedText>
                <Ionicons
                  name="chevron-forward"
                  size={18}
                  color={c.textMuted}
                />
              </View>
              <ThemedText
                variant={card.empty ? "bodyStrong" : "title"}
                numberOfLines={2}
                style={styles.cardPrimary}
              >
                {card.primary}
              </ThemedText>
              <View style={styles.cardSupporting}>
                {card.supporting.map((line) => (
                  <ThemedText
                    key={line}
                    variant="caption"
                    tone="secondary"
                    numberOfLines={2}
                  >
                    {line}
                  </ThemedText>
                ))}
              </View>
            </StoryCardSurface>
          </Pressable>
        );
      })}
    </View>
  );
}

function StoryCardSurface({ children }: { children: ReactNode }) {
  const c = useTheme();
  return (
    <View
      style={[
        styles.cardSurface,
        { backgroundColor: c.surfaceAlt, borderColor: c.border },
      ]}
    >
      {children}
    </View>
  );
}

function IconTile({ name }: { name: keyof typeof Ionicons.glyphMap }) {
  const c = useTheme();
  return (
    <View style={[styles.iconTile, { backgroundColor: c.backgroundSelected }]}>
      <Ionicons name={name} size={19} color={c.tint} />
    </View>
  );
}

export function ProgressDetailHeader({
  detail,
}: {
  detail: ProgressDetailKey;
}) {
  return (
    <View style={styles.detailHeader}>
      <View style={styles.detailTitleRow}>
        <IconTile name={CARD_META[detail].icon} />
        <ThemedText variant="title">
          {i18n.t(CARD_META[detail].labelKey)}
        </ThemedText>
      </View>
    </View>
  );
}

export function PracticeProgressDetail({
  data,
}: {
  data: ProgressDashboard["practice"];
}) {
  return (
    <View style={styles.detailBody}>
      <MetricStrip
        items={[
          {
            value: data.completed,
            label: i18n.t("growth.story.practice.completed"),
          },
          {
            value: data.activePractice,
            label: i18n.t("growth.story.practice.active"),
          },
        ]}
      />

      {data.categories.length > 0 ? (
        <PagedEvidence
          title={i18n.t("growth.story.practice.bySkill")}
          items={data.categories}
          renderItem={(category) => (
            <EvidenceRow
              key={category.key}
              label={category.label}
              value={String(category.count)}
            />
          )}
        />
      ) : (
        <EmptyEvidence text={i18n.t("growth.story.practice.emptyBody")} />
      )}

      {data.recent.length > 0 ? (
        <PagedEvidence
          title={i18n.t("growth.story.practice.recent")}
          items={data.recent}
          renderItem={(session) => (
            <EvidenceRow
              key={session.id}
              label={session.categoryLabel}
              value={dateTime(session.completedAt)}
            />
          )}
        />
      ) : null}
    </View>
  );
}

/**
 * Completed and Started follow the Week/Month selector; Active now is the
 * live count and stays put, which the caption spells out so the three tiles
 * don't look inconsistent.
 */
export function GoalsProgressDetail({
  data,
  rangeDays,
}: {
  data: ProgressDashboard["goals"];
  rangeDays: 7 | 30;
}) {
  // Two whole sentences, not one with a {{period}} noun dropped in: "this
  // week" and "this month" need determiners that agree in French and
  // Portuguese, which no interpolation of a bare noun can express.
  const monthly = rangeDays === 30;
  return (
    <View style={styles.detailBody}>
      <MetricStrip
        items={[
          {
            value: data.completed,
            label: i18n.t("growth.story.goals.completed"),
          },
          { value: data.started, label: i18n.t("growth.story.goals.started") },
          {
            value: data.activeNow,
            label: i18n.t("growth.story.goals.activeNow"),
          },
        ]}
      />
      <ThemedText variant="caption" tone="secondary">
        {i18n.t(
          monthly
            ? "growth.story.goals.rangeNoteMonth"
            : "growth.story.goals.rangeNoteWeek",
        )}
      </ThemedText>
      {data.completedGoals.length > 0 ? (
        <PagedEvidence
          title={i18n.t("growth.story.goals.followedThrough")}
          items={data.completedGoals}
          hasMore={data.completedGoalsHasMore}
          renderItem={(goal) => (
            <CompletedGoalRow
              key={goal.id}
              title={goal.title}
              completedAt={goal.completedAt}
            />
          )}
        />
      ) : (
        <EvidenceSection title={i18n.t("growth.story.goals.followedThrough")}>
          <EmptyEvidence
            text={i18n.t(
              monthly
                ? "growth.story.goals.noneThisMonth"
                : "growth.story.goals.noneThisWeek",
            )}
          />
        </EvidenceSection>
      )}
    </View>
  );
}

function CompletedGoalRow({
  title,
  completedAt,
}: {
  title: string;
  completedAt: string;
}) {
  const c = useTheme();
  return (
    <View style={[styles.completedGoalRow, { borderBottomColor: c.border }]}>
      <ThemedText variant="body" numberOfLines={3}>
        {i18n.t("growth.story.goals.goalTitle", { title })}
      </ThemedText>
      <ThemedText variant="caption" tone="secondary">
        {i18n.t("growth.story.goals.completedOn", {
          date: completionDate(completedAt),
        })}
      </ThemedText>
    </View>
  );
}

/**
 * Counters only. The reflections themselves are listed once, by
 * `ReflectionHistoryList` below this — that list is filterable and paged over
 * the whole journal, so duplicating a short preview here just showed the same
 * entries twice from a second data source.
 */
export function ReflectionsProgressDetail({
  data,
}: {
  data: ProgressDashboard["reflections"];
}) {
  return (
    <View style={styles.detailBody}>
      <MetricStrip
        items={[
          { value: data.total, label: i18n.t("growth.story.reflections.logged") },
          {
            value: data.insideOpncue,
            label: i18n.t("growth.story.reflections.inside"),
          },
          {
            value: data.outsideOpncue,
            label: i18n.t("growth.story.reflections.outside"),
          },
        ]}
      />
    </View>
  );
}

function MetricStrip({
  items,
}: {
  items: { value: number; label: string }[];
}) {
  const c = useTheme();
  return (
    <View style={styles.metricStrip}>
      {items.map((item) => (
        <View
          key={item.label}
          style={[
            styles.metricTile,
            { backgroundColor: c.surfaceAlt, borderColor: c.border },
          ]}
        >
          <ThemedText variant="title">{item.value}</ThemedText>
          <ThemedText variant="caption" tone="secondary" numberOfLines={2}>
            {item.label}
          </ThemedText>
        </View>
      ))}
    </View>
  );
}

/**
 * An evidence list shown five rows at a time.
 *
 * These lists have no natural ceiling — a month of completed goals or practice
 * sessions is however many the person did — and every one of them sits inside
 * the same scrolling sheet, so rendering all of them buries whatever comes
 * next under a wall of rows.
 */
function PagedEvidence<T>({
  title,
  items,
  renderItem,
  hasMore = false,
}: {
  title: string;
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  /**
   * The server capped this list. There is no "load more" to offer — the pager
   * shows a trailing "…" so the last page never reads as the end of the story,
   * and leaves its arrow disabled because nothing here can fetch the rest.
   */
  hasMore?: boolean;
}) {
  const { page, totalPages, visible, setPage } = usePagedItems(items);
  return (
    <EvidenceSection title={title}>
      <View>{visible.map(renderItem)}</View>
      <Pager
        page={page}
        totalPages={totalPages}
        onChange={setPage}
        hasMore={hasMore}
      />
    </EvidenceSection>
  );
}

function EvidenceSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <View style={styles.evidenceSection}>
      <ThemedText variant="label" tone="muted">
        {title.toUpperCase()}
      </ThemedText>
      <View>{children}</View>
    </View>
  );
}

function EvidenceRow({ label, value }: { label: string; value: string }) {
  const c = useTheme();
  return (
    <View style={[styles.evidenceRow, { borderBottomColor: c.border }]}>
      <ThemedText variant="body" style={styles.evidenceCopy} numberOfLines={3}>
        {label}
      </ThemedText>
      <ThemedText
        variant="caption"
        tone="secondary"
        style={styles.evidenceValue}
        numberOfLines={2}
      >
        {value}
      </ThemedText>
    </View>
  );
}

function EmptyEvidence({ text }: { text: string }) {
  const c = useTheme();
  return (
    <View
      style={[
        styles.empty,
        { backgroundColor: c.surfaceAlt, borderColor: c.border },
      ]}
    >
      <ThemedText variant="caption" tone="secondary">
        {text}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.md,
  },
  card: { minWidth: 0 },
  cardThreeColumn: { flexBasis: 0, flexGrow: 1, flexShrink: 1 },
  cardSingleColumn: { flexBasis: "100%" },
  cardSurface: {
    minHeight: 154,
    gap: Spacing.sm,
    padding: Spacing.lg,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.sm,
  },
  iconTile: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.md,
  },
  cardPrimary: { letterSpacing: 0 },
  cardSupporting: { gap: 3 },
  pressed: { opacity: 0.78 },
  detailHeader: { gap: Spacing.lg },
  detailTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
  },
  detailBody: { gap: Spacing.lg },
  metricStrip: { flexDirection: "row", flexWrap: "wrap", gap: Spacing.sm },
  metricTile: {
    flexGrow: 1,
    flexBasis: "22%",
    minWidth: 104,
    gap: 2,
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
  evidenceSection: { gap: Spacing.sm },
  evidenceRow: {
    minHeight: 50,
    flexDirection: "row",
    alignItems: "center",
    gap: Spacing.md,
    paddingVertical: Spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  completedGoalRow: {
    // Stacked rather than label/value: the completion date belongs under the
    // goal, and a long goal title would otherwise squeeze it off the row.
    gap: 2,
    paddingVertical: Spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  evidenceCopy: { flex: 1, minWidth: 0 },
  evidenceValue: { maxWidth: "44%", textAlign: "right" },
  empty: {
    padding: Spacing.md,
    borderRadius: Radius.lg,
    borderWidth: StyleSheet.hairlineWidth,
  },
});
